import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getFilterLocation } from '../selectors/filters/getFilterLocation';
import { type ArrayOfObjects } from '../types/ArrayOfObjects';
import { useApplicationContext } from '../utils/applicationContext';
import { getIterableArrayOfObjects } from '../utils/getIterableArrayOfObjects';

import { LOCATION } from './regions';

type PhotoScheme = {
  title: string;
  imageSrc: string;
  description: string;
};

export type SeriaPlusScheme = {
  watchObjectsLink: string | undefined;
  title: string;
  sectionTabTitle: string;
  media: ArrayOfObjects<PhotoScheme>;
  description: string;
};

export type SeriaPlusByLocationScheme = Record<number, SeriaPlusScheme>;

const media = getIterableArrayOfObjects<PhotoScheme>([
  {
    description:
      'Наслаждайтесь ужинами под открытым небом, работайте удалённо с видом на город или просто отдыхайте, любуясь окружающим пейзажем. Для вашего удобства на террасе выполнена гидроизоляция пола, проведено освещение и установлены розетки.',
    imageSrc: 'https://static.cdn-cian.ru/frontend/newbuilding-b2b-assets/images/pik/seria_plus_v2/terraces.jpg',
    title: 'Террасы',
  },
  {
    description:
      'Естественный свет — важнейшее условие здоровья и продуктивности. Поэтому дома «Серии плюс» отличаются разнообразием остекления. Окна наполняют квартиру дневным светом и делают пространство более уютным.',
    imageSrc:
      'https://static.cdn-cian.ru/frontend/newbuilding-b2b-assets/images/pik/seria_plus_v2/floor-to-ceiling-windows.jpg',
    title: 'Окна в пол',
  },
  {
    description:
      'В кварталах «Серии плюс» представлены балконы разных форм: полукруглые, трапециевидные, угловые. Снаружи они придают фасадам особую динамику, а в квартире появляется дополнительная зона отдыха.',
    imageSrc: 'https://static.cdn-cian.ru/frontend/newbuilding-b2b-assets/images/pik/seria_plus_v2/balconies.jpg',
    title: 'Балконы',
  },
  {
    description:
      'Просторные, светлые лобби с высокими потолками и витражным остеклением ежедневно встречают жителей в кварталах «Серии плюс». Продуманная планировка включает удобную зону для ожидания, гостевой санузел и место для хранения колясок и велосипедов.',
    imageSrc: 'https://static.cdn-cian.ru/frontend/newbuilding-b2b-assets/images/pik/seria_plus_v2/modern-lobby.jpg',
    title: 'Современное лобби',
  },
  {
    description:
      'Каждая деталь отличается функциональностью и эстетикой. Индивидуальная пластика фасадов создаёт современный архитектурный облик, а панорамное остекление делает солнце частью вашего интерьера.',
    imageSrc:
      'https://static.cdn-cian.ru/frontend/newbuilding-b2b-assets/images/pik/seria_plus_v2/thoughtful-architecture.jpg',
    title: 'Продуманная архитектура',
  },
]);

const title = 'О «Серии плюс»';
const sectionTabTitle = 'Серия плюс';
const description =
  '«Серия плюс» — кварталы ПИК для тех, кто ценит индивидуальность: проекты объединяют уникальную выразительную архитектуру и тщательно спланированную инфраструктуру.';

export function useSeriaPlusByLocation(): SeriaPlusScheme | null {
  const { config } = useApplicationContext();

  const location = useSelector(getFilterLocation);

  return useMemo(() => {
    const watchObjectsLinkByLocation = config.get<{ [key: string]: string }>(
      'PikLanding.SeriaPrus.WatchObjectsLinkByLocation',
    );

    const watchObjectsLink = watchObjectsLinkByLocation?.[location];

    switch (location) {
      case LOCATION.MOSCOW_AND_AREA.ID:
      case LOCATION.SPB_AND_AREA.ID:
        return {
          description,
          media,
          sectionTabTitle,
          title,
          watchObjectsLink,
        };

      default:
        return null;
    }
    // Зависит только от локации
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
}
